// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { createApi } from '@reduxjs/toolkit/query/react';
import { fetchQuery } from '@shared/data-access/queries';
import { SHARED_TAG_TYPES } from '@shared/data-access/utils';
import { environment } from '@shared/environment';

/**
 * Engine tag types enum.
 *
 * Centralized enum for engine-related cache tag types used in RTK Query V2. Only
 * used by `engineApiV2`. Legacy `engineApi` uses string literals.
 */
export enum EngineTagTypes {
  Tag = 'Tag',
  TagAssignment = 'TagAssignment',
  IlliquidAsset = 'IlliquidAsset',
  Document = 'Document',
  Onboarding = 'Onboarding',
}

/**
 * Legacy Engine API configuration using Redux Toolkit Query.
 *
 * Used by watchlist, ticker-tag, portfolio and other endpoints until migrated to
 * engineApiV2. New tag/tag-assignment endpoints use engineApiV2.
 */
export const engineApi = createApi({
  baseQuery: fetchQuery({
    baseUrl: environment.API.ENGINE_BASE_URL,
    authTokenUsage: 'header',
  }),
  endpoints: () => ({}),
  reducerPath: 'engineApi',
  tagTypes: [
    'User',
    'Watchlist',
    'TickerTag',
    'Subscription',
    'Portfolio',
    ...SHARED_TAG_TYPES,
  ],
});

/**
 * Engine API V2 configuration using Redux Toolkit Query.
 *
 * New API version with type-safe cache tags (EngineTagTypes). Holds tags,
 * tag-assignments, and illiquid-assets endpoints (raw-api + processed APIs) until
 * the rest are migrated from engineApi.
 */
export const engineApiV2 = createApi({
  baseQuery: fetchQuery({
    baseUrl: environment.API.ENGINE_BASE_URL,
    authTokenUsage: 'header',
  }),
  endpoints: () => ({}),
  reducerPath: 'engineApiV2',
  tagTypes: [
    EngineTagTypes.Tag,
    EngineTagTypes.TagAssignment,
    EngineTagTypes.IlliquidAsset,
    EngineTagTypes.Document,
    EngineTagTypes.Onboarding,
    ...SHARED_TAG_TYPES,
  ],
});
